import React, { useState, useMemo, useCallback, useEffect, createContext, useContext, useRef } from 'react';

var __assign = (undefined && undefined.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var Validators = {
    required: function (value) {
        if (value === null || value === undefined) {
            return { required: true };
        }
        if (Array.isArray(value) && value.length === 0) {
            return { required: true };
        }
        if (typeof value === "string" && value === "") {
            return { required: true };
        }
        return null;
    },
    min: function (min) { return function (value) {
        if (value < min) {
            return { min: { min: min, actualValue: value } };
        }
        return null;
    }; },
    max: function (max) { return function (value) {
        if (value > max) {
            return { max: { max: max, actualValue: value } };
        }
        return null;
    }; },
    maxLength: function (length) { return function (value) {
        if (value.length > length) {
            return { maxLength: { maxLength: length, actualLength: value.length } };
        }
        return null;
    }; },
    minLength: function (length) { return function (value) {
        if (value.length < length) {
            return { minLength: { minLength: length, actualLength: value.length } };
        }
        return null;
    }; },
    format: function (regexp) { return function (value) {
        if (!value.match(regexp)) {
            return { format: true };
        }
        return null;
    }; },
};
var mergeValidators = function (validators) {
    if (!validators) {
        return function (_value) { return null; };
    }
    if (!Array.isArray(validators)) {
        return validators;
    }
    return function (value) {
        var errors = validators.reduce(function (acc, validator) {
            var error = validator(value);
            return __assign(__assign({}, acc), error);
        }, {});
        return Object.keys(errors).length === 0 ? null : errors;
    };
};

var __assign$1 = (undefined && undefined.__assign) || function () {
    __assign$1 = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign$1.apply(this, arguments);
};
function initMeta(values) {
    var meta = {};
    Object.keys(values).forEach(function (key) {
        meta[key] = {
            pristine: true,
            dirty: false,
            touched: false,
            untouched: true,
        };
    });
    return meta;
}
function initValidators(validators) {
    if (!validators) {
        return { validators: {} };
    }
    var mergedValidators = {};
    Object.keys(validators).forEach(function (key) {
        var validator = validators[key];
        mergedValidators[key] = mergeValidators(validator);
    });
    return { validators: mergedValidators };
}
function initErrors(values) {
    var errors = {};
    Object.keys(values).forEach(function (key) {
        errors[key] = null;
    });
    return errors;
}
function useFormGroup(formGroupOptions) {
    var initialValues = useState(formGroupOptions.values)[0];
    var _a = useState(formGroupOptions.values), values = _a[0], setValues = _a[1];
    var _b = useState(initMeta(formGroupOptions.values)), metaInfos = _b[0], setMetaInfo = _b[1];
    var _c = useState(initErrors(formGroupOptions.values)), errors = _c[0], setErrors = _c[1];
    var _d = useState("VALID"), status = _d[0], setStatus = _d[1];
    var validators = useMemo(function () { return initValidators(formGroupOptions.validators); }, [formGroupOptions]).validators;
    // TODO: Validate initial values on mount
    var reset = useCallback(function () {
        setValues(initialValues);
        setMetaInfo(initMeta(initialValues));
        setErrors({});
        // eslint-disable-next-line
    }, []);
    var setValue = useCallback(function (keysAndValues) {
        var updatedValues = {};
        var updatedMeta = {};
        var newErrors = {};
        Object.keys(keysAndValues).forEach(function (key) {
            updatedValues[key] = keysAndValues[key];
            updatedMeta[key] = {
                pristine: updatedValues[key] === initialValues[key],
                dirty: updatedValues[key] !== initialValues[key],
                touched: true,
                untouched: false,
            };
            var validator = validators[key];
            if (validator) {
                newErrors[key] = validator(updatedValues[key]);
            }
        });
        setValues(function (currentValues) { return (__assign$1(__assign$1({}, currentValues), updatedValues)); });
        setMetaInfo(function (currentMetas) { return (__assign$1(__assign$1({}, currentMetas), updatedMeta)); });
        setErrors(function (currentErrors) { return (__assign$1(__assign$1({}, currentErrors), newErrors)); });
    }, [initialValues, validators]);
    var setTouchedOnBlur = useCallback(function (key) {
        setMetaInfo(function (currentMetas) {
            var _a;
            return (__assign$1(__assign$1({}, currentMetas), (_a = {}, _a[key] = __assign$1(__assign$1({}, currentMetas[key]), { touched: true, untouched: false }), _a)));
        });
    }, []);
    useEffect(function () {
        var hasError = Boolean(Object.values(errors).filter(function (e) { return e !== null && Object.keys(e).length > 0; }).length);
        setStatus(hasError ? "INVALID" : "VALID");
    }, [errors]);
    return {
        status: status,
        setValue: setValue,
        metaInfos: metaInfos,
        values: values,
        errors: errors,
        reset: reset,
        setTouchedOnBlur: setTouchedOnBlur,
    };
}

var FieldContext = createContext(null);
function FormGroupProvider(_a) {
    var formGroup = _a.formGroup, children = _a.children;
    return React.createElement(FieldContext.Provider, { value: formGroup }, children);
}

var __assign$2 = (undefined && undefined.__assign) || function () {
    __assign$2 = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign$2.apply(this, arguments);
};
function useFormControl(name) {
    var formGroup = useContext(FieldContext);
    var inputRef = useRef(null);
    var textareaRef = useRef(null);
    var selectRef = useRef(null);
    if (formGroup === null) {
        throw new Error(['Could not find "formGroup" in context.', "Wrap the root component in a <FormGroupProvider>."].join(" "));
    }
    var values = formGroup.values, metaInfos = formGroup.metaInfos, errors = formGroup.errors, setTouchedOnBlur = formGroup.setTouchedOnBlur;
    var setValue = useCallback(function (value) {
        var _a;
        return formGroup.setValue((_a = {}, _a[name] = value, _a));
    }, [formGroup, name]);
    useEffect(function () {
        var element = inputRef.current || textareaRef.current || selectRef.current;
        if (!element) {
            return;
        }
        var onBlur = function () { return setTouchedOnBlur(name); };
        element.addEventListener("blur", onBlur);
        return function () { return element.removeEventListener("blur", onBlur); };
        // NOTE: Call this effect onMount only.
        /* eslint-disable-next-line */
    }, []);
    return __assign$2(__assign$2({ value: values[name] }, metaInfos[name]), { errors: errors[name], setValue: setValue,
        inputRef: inputRef,
        selectRef: selectRef,
        textareaRef: textareaRef });
}

var __assign$3 = (undefined && undefined.__assign) || function () {
    __assign$3 = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign$3.apply(this, arguments);
};
var __spreadArrays = (undefined && undefined.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
function useFormArray(name) {
    var formGroup = useContext(FieldContext);
    var formControl = useFormControl(name);
    var addOrRemoveValue = useCallback(function (_a) {
        var _b;
        var value = _a.value, checked = _a.checked;
        // NOTE: The formGroup must not be null, because useFormControl checks it and throws exception if formGroup is null.
        var currentValues = formGroup.values[name];
        formGroup.setValue((_b = {},
            _b[name] = checked ? __spreadArrays(currentValues, [value]) : __spreadArrays(currentValues).filter(function (v) { return v !== value; }),
            _b));
    }, [formGroup, name]);
    var hasValue = useCallback(function (value) { return formGroup.values[name].indexOf(value) !== -1; }, [formGroup, name]);
    return __assign$3(__assign$3({}, formControl), { addOrRemoveValue: addOrRemoveValue,
        hasValue: hasValue });
}

var __assign$4 = (undefined && undefined.__assign) || function () {
    __assign$4 = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign$4.apply(this, arguments);
};
var __rest = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var FieldControl = function (props) {
    var name = props.name, children = props.children;
    var _a = useFormControl(name), value = _a.value, errors = _a.errors, setValue = _a.setValue, meta = __rest(_a, ["value", "errors", "setValue"]);
    return children(__assign$4({ value: value,
        errors: errors,
        setValue: setValue }, meta));
};

export { FieldContext, FieldControl, FormGroupProvider, Validators, initErrors, initMeta, initValidators, mergeValidators, useFormArray, useFormControl, useFormGroup };
